import React from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import {
  Timeline as TimelineIcon,
  CheckCircle as SuccessIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { formatDate } from './utils';

/**
 * VersionTimelineCard Component
 *
 * Displays version timeline events.
 *
 * @param {Object} props
 * @param {Object} props.selectedVersion - Selected version data
 */
function VersionTimelineCard({ selectedVersion }) {
  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" mb={3}>
          <TimelineIcon color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6">Version Timeline</Typography>
        </Box>
        <Timeline position="right" sx={{ p: 0 }}>
          {selectedVersion.version_created_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.version_created_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="primary">
                  <TimelineIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  Version Created
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Version {selectedVersion.version_number} was created
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.first_execution_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.first_execution_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="success">
                  <SuccessIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  First Execution
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  First successful execution of this version
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.first_feedback_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.first_feedback_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="warning">
                  <StarIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  First Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  First user feedback received
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.last_feedback_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.last_feedback_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="warning" variant="outlined">
                  <StarIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  Last Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Most recent user feedback
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.last_execution_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.last_execution_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="success" variant="outlined">
                  <SuccessIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  Last Execution
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Most recent execution of this version
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.version_updated_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.version_updated_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="info">
                  <TimelineIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  Version Updated
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last update to version {selectedVersion.version_number}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
          {selectedVersion.last_activity_at && (
            <TimelineItem>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {formatDate(selectedVersion.last_activity_at)}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="primary" variant="outlined">
                  <TimelineIcon sx={{ fontSize: '1rem' }} />
                </TimelineDot>
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  Last Activity
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Most recent activity for this version
                </Typography>
              </TimelineContent>
            </TimelineItem>
          )}
        </Timeline>
      </CardContent>
    </Card>
  );
}

export default VersionTimelineCard;
